import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Menu,
  MenuItem,
  Button,
  Divider,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactComponent as ArrowDropDownIcon } from "../../../assets/icons/arrow-drop-down-icon.svg";
import UserContext from "../../../context/userContext";
import { getUserDisplayName } from "../../../utils";
import { FONT_INTER } from "../../../styles/fonts";
import {
  focusRingInsetSx,
  focusRingOnDarkSx,
} from "../../../styles/focusRing";
import visuallyHiddenSx from "../../../utils/a11y/visuallyHiddenSx";

const triggerButtonSx = {
  minWidth: 0,
  height: 32,
  px: "4px",
  color: "#FFFFFF",
  border: "1px solid #FFFFFF",
  borderRadius: "6px",
  fontFamily: FONT_INTER,
  fontSize: 12,
  fontWeight: 500,
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: "9px",
    ml: 0,
    // MUI forces font-size: 20px on the first startIcon child, which beats the Avatar's own sx
    "& > *:nth-of-type(1)": {
      fontSize: 11,
    },
  },
  "& .MuiButton-endIcon": {
    ml: "9px",
    mr: "5px",
  },
  "&:hover": {
    bgcolor: "rgba(255, 255, 255, 0.12)",
    borderColor: "#FFFFFF",
  },
  "&.MuiButtonBase-root:focus-visible": focusRingOnDarkSx,
};

const paperSx = {
  mt: 0.5,
  p: "0 8px",
  minWidth: 280,
  borderRadius: "10px",
  fontFamily: FONT_INTER,
  boxShadow: "0px 4px 8px 2px rgba(0, 0, 0, 0.12)",
  overflow: "hidden",
};

const nameRowSx = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  px: "8px",
  py: "8px",
  my: "4px",
  borderRadius: "4px",
};

const avatarSx = {
  width: 40,
  height: 40,
  bgcolor: "#F5F5F5",
  color: "#4D4D4D",
  fontFamily: FONT_INTER,
  fontSize: 16,
  fontWeight: 400,
};

const triggerAvatarSx = {
  width: 24,
  height: 24,
  borderRadius: "4px",
  bgcolor: "#E6E6E6",
  color: "#4D4D4D",
  fontFamily: FONT_INTER,
  fontSize: 11,
  fontWeight: 500,
};

const nameTextSx = {
  fontFamily: FONT_INTER,
  fontSize: 14,
  fontWeight: 500,
  color: "#1A1A1A",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const emailTextSx = {
  fontFamily: FONT_INTER,
  fontSize: 12,
  color: "#666666",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Always 2 letters: first + last name initials, or the first two letters of a single name
const getInitials = (fullName) => {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const logoutRowSx = {
  display: "flex",
  width: "100%",
  px: "8px",
  py: "6px",
  mt: "4px",
  borderRadius: "4px",
  color: "error.main",
  fontFamily: FONT_INTER,
  fontSize: 14,
  fontWeight: 500,
  "&:hover": {
    bgcolor: "action.hover",
  },
  "&.Mui-focusVisible": focusRingInsetSx,
};

// Menu treats the first non-disabled child as focusable, so this must opt out like Divider does (Divider.muiSkipListHighlight) or it steals focus/tabindex from "Sair"
function AccountSummaryRow(props) {
  return <Box {...props} />;
}
AccountSummaryRow.muiSkipListHighlight = true;

let menuIdCounter = 0;

export const UserMenu = ({ userName, onLogout }) => {
  const { me } = useContext(UserContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuId] = useState(() => `user-menu-${++menuIdCounter}`);

  const resolvedUserName =
    userName ?? (!me.loading ? getUserDisplayName(me.user?.name) : "");

  const fullName = me.user?.name || userName || "";
  const email = me.user?.email;

  const open = Boolean(anchorEl);
  const triggerId = `${menuId}-trigger`;

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    onLogout ? onLogout() : navigate("/logout");
  };

  if (!resolvedUserName) return null;

  return (
    <>
      <Tooltip title="Menu de Usuário" describeChild>
        <Button
          id={triggerId}
          onClick={handleOpen}
          disableRipple
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          startIcon={
            <Avatar sx={triggerAvatarSx} aria-hidden="true">
              {getInitials(fullName)}
            </Avatar>
          }
          endIcon={<ArrowDropDownIcon aria-hidden="true" focusable="false" />}
          sx={triggerButtonSx}
        >
          {resolvedUserName}
        </Button>
      </Tooltip>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-labelledby": triggerId }}
        PaperProps={{ sx: paperSx }}
      >
        <AccountSummaryRow sx={nameRowSx}>
          <Avatar sx={avatarSx} aria-hidden="true">
            {getInitials(fullName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={nameTextSx}>
              <Box component="span" sx={visuallyHiddenSx}>
                Conta:{" "}
              </Box>
              {fullName}
            </Typography>
            {email && <Typography sx={emailTextSx}>{email}</Typography>}
          </Box>
        </AccountSummaryRow>

        <Divider sx={{ my: "8px" }} />

        <MenuItem onClick={handleLogout} sx={logoutRowSx}>
          Sair
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
